import React, { useState, useEffect } from 'react';
import { UPGRADES } from '../content/upgrades';
import { PLAYER, RESOURCES } from '../data';

export default function Upgrades() {
  const [error, setError] = useState('');
  const [resources, setResources] = useState(PLAYER.resources);
  const [upgrades, setUpgrades] = useState(PLAYER.upgrades || {});

  useEffect(() => {
    const interval = setInterval(() => {
      setResources({ ...PLAYER.resources });
      setUpgrades({ ...PLAYER.upgrades || {} });
    }, 10);

    return () => clearInterval(interval);
  }, []);

  const getEffect = (upgrade, level) => {
    return upgrade.baseValue + (level * upgrade.valuePerLevel);
  };

  const formatEffect = (upgrade, value) => {
    return `${typeof value === 'number' ? value.toFixed(1) : value} ${upgrade.formatString}`;
  };

  const purchaseUpgrade = (upgradeId) => {
    const upgrade = UPGRADES[upgradeId];
    const currentLevel = upgrades[upgradeId] || 0;

    if (currentLevel >= upgrade.maxLevel) {
      return;
    }

    const cost = upgrade.baseCost * Math.pow(upgrade.costMultiplier, currentLevel);

    if (resources.currency >= cost) {
      PLAYER.resources.currency -= cost;
      PLAYER.upgrades = PLAYER.upgrades || {};
      PLAYER.upgrades[upgradeId] = (PLAYER.upgrades[upgradeId] || 0) + 1;

      // Update the corresponding player stat using the statPath
      if (upgrade.statName) {
        PLAYER.stats[upgrade.statName] = getEffect(upgrade, PLAYER.upgrades[upgradeId]);
      }

      setError('');
    } else {
      setError(`Not enough ${RESOURCES.currency.name}! Need ${cost} ${RESOURCES.currency.name}.`);
    }
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Upgrades</h1>
        <div className="text-xl">
          Current {RESOURCES.currency.name}: {resources.currency || 0}
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-4">
          {error}
        </div>
      )}

      <div className="grid gap-4">
        {Object.values(UPGRADES).map(upgrade => {
          const currentLevel = upgrades[upgrade.id] || 0;
          const cost = upgrade.baseCost * Math.pow(upgrade.costMultiplier, currentLevel);
          const effect = getEffect(upgrade, currentLevel);
          const nextEffect = getEffect(upgrade, currentLevel + 1);
          const canAfford = resources.currency >= cost;

          return (
            <div key={upgrade.id} className="border p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-bold">{upgrade.name}</h3>
                  <p className="text-sm text-gray-600">{upgrade.description}</p>
                  <p className="text-sm">Current: {formatEffect(upgrade, effect)}</p>
                  {currentLevel < upgrade.maxLevel && (
                    <p className="text-sm">Next: {formatEffect(upgrade, nextEffect)}</p>
                  )}
                </div>
                <button
                  className={`px-4 py-2 rounded ${
                    currentLevel >= upgrade.maxLevel
                      ? 'bg-gray-400 cursor-not-allowed'
                      : canAfford
                      ? 'bg-blue-500 hover:bg-blue-600 text-white'
                      : 'bg-red-500 cursor-not-allowed opacity-50 text-white'
                  }`}
                  onClick={() => purchaseUpgrade(upgrade.id)}
                  disabled={currentLevel >= upgrade.maxLevel || !canAfford}
                >
                  {currentLevel >= upgrade.maxLevel
                    ? 'Maxed'
                    : `${cost} ${RESOURCES.currency.name}`}
                </button>
              </div>
              <div className="mt-2 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 rounded-full h-2"
                  style={{width: `${(currentLevel / upgrade.maxLevel) * 100}%`}}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}